import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import {
    dark_grey_blue,
    silver_two,
    faded_red,
    cool_grey,
    color_colors_ocean,
    color_variants_ocean_light_2,
    color_colors_decline,
    color_variants_red_2,
    color_shades_darkest,
    color_variants_ocean_light_1,
    color_variants_ocean_xlight,
    text_blue
} from '../../constants/colors';
import FieldError from './FieldError';
import Button from '../general/Button';
import IconTooltip from '../general/IconTooltip';
import TooltipWrapper from '../general/TooltipWrapper';
import CurrentApplicationContainer from '../../hooks/CurrentApplicationContainer';
import ApplicationVariablesContainer from '../../hooks/ApplicationVariablesContainer';
import ApplicationVariable from '../../models/applications/ApplicationVariable';

// Icons
const deleteIcon = require('../../content/images/trash.svg');
const editIcon = require('../../content/images/pencil.svg');

// Types and Interfaces
interface TextFieldProps {
    name?: string;
    type?: string;
    placeholder?: string;
    value?: string;
    disabled?: boolean;
    onChange?: (e: React.ChangeEvent<any>) => void;
    onBlur?: (e: any) => void;
    onFocus?: (e: any) => void;
    onDelete?: () => void;
    label?: string;
    id?: string;
    canDelete?: boolean;
    required?: boolean;
    tooltip?: string;
    className?: string;
    icon?: string;
    minRows?: number;
    maxRows?: number;
    useForm?: boolean;
    stopPropagation?: boolean;
    defaultValue?: string;
    onReset?: () => void;
}

interface TooltipState {
    text: string;
    x: number;
    y: number;
    visible: boolean;
    editable: boolean;
    variableText?: string;
    variableName?: string;
}

interface VariableReference {
    start: number;
    end: number;
    text: string;
    prefix: string;
    variableName: string;
    variable?: ApplicationVariable;
    isValid: boolean;
}

const TextAreaField: React.FC<TextFieldProps> = (props) => {
    // Container hooks
    const currentApplicationContainer = CurrentApplicationContainer.useContainer();
    const appVariableContainer = ApplicationVariablesContainer.useContainer();

    // State
    const [tooltip, setTooltip] = useState<TooltipState>({
        text: '',
        x: 0,
        y: 0,
        visible: false,
        editable: false
    });

    // Refs
    const contentEditableRef = useRef<HTMLDivElement>(null);
    const lastValueRef = useRef<string>('');
    const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isHoveringTooltipRef = useRef<boolean>(false);
    const isHoveringVariableRef = useRef<boolean>(false);

    // Effects - moved to top
    useEffect(() => {
        if (currentApplicationContainer.currentApplicationId) {
            appVariableContainer.loadApplicationVariablesByApplicationId(currentApplicationContainer.currentApplicationId)
                .catch((error) => {
                    // Error loading variables - silently fail
                });
        }
    }, [currentApplicationContainer.currentApplicationId]);

    useEffect(() => {
        // Update content when value changes externally or when variables are loaded
        if (contentEditableRef.current && props.value !== lastValueRef.current) {
            // Only update if the element doesn't have focus to avoid cursor jumping during typing
            if (document.activeElement !== contentEditableRef.current) {
                contentEditableRef.current.innerHTML = generateHighlightedHTML(props.value || '');
                lastValueRef.current = props.value || '';
                attachTooltipEvents();
            }
            // If element has focus, don't update anything - let handleContentChange manage it
        }
    }, [props.value, appVariableContainer.applicationVariables]);

    useEffect(() => {
        // Force re-render when variables are loaded to update highlighting
        if (contentEditableRef.current && lastValueRef.current && appVariableContainer.applicationVariables.length > 0) {
            // Save cursor position if element has focus
            const hasFocus = document.activeElement === contentEditableRef.current;
            const currentCursorPos = hasFocus ? saveCursorPosition() : 0;

            contentEditableRef.current.innerHTML = generateHighlightedHTML(lastValueRef.current);
            attachTooltipEvents();

            // Restore cursor if it had focus
            if (hasFocus) {
                setTimeout(() => {
                    restoreCursorPosition(currentCursorPos);
                }, 0);
            }
        }
    }, [appVariableContainer.applicationVariables]);

    useEffect(() => {
        // Initialize content on mount
        if (contentEditableRef.current && props.value) {
            contentEditableRef.current.innerHTML = generateHighlightedHTML(props.value);
            lastValueRef.current = props.value;
            attachTooltipEvents();
        }

        // Cleanup timeout on unmount
        return () => {
            if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
            }
        };
    }, []);

    // Computed values
    const showLabel = props.label !== undefined || props.canDelete || props.tooltip !== undefined || props.required;
    const showResetButton = props.defaultValue !== undefined && props.value !== props.defaultValue;

    // Event handlers
    const handleReset = () => {
        if (props.onReset) {
            props.onReset();
        } else if (props.onChange) {
            const syntheticEvent = {
                target: { value: props.defaultValue, name: props.name },
                currentTarget: { value: props.defaultValue, name: props.name }
            } as React.ChangeEvent<any>;
            props.onChange(syntheticEvent);
        }
    };

    // Utility functions for variable processing

    // Check if text contains variable references
    const hasVariableReferences = (text: string): boolean => {
        const regex = /\{\{(variable|prompt|constant)\.([A-Za-z0-9_]+)\}\}/;
        return regex.test(text);
    };

    // Recursively resolve nested variables to get the deepest value
    const resolveNestedVariables = (text: string, maxDepth: number = 10): string => {
        if (maxDepth <= 0) return text; // Prevent infinite recursion

        const constants = Array.isArray(appVariableContainer.applicationVariables)
            ? appVariableContainer.applicationVariables
            : [];

        const regex = /\{\{(variable|prompt|constant)\.([A-Za-z0-9_]+)\}\}/g;
        let hasVariables = false;

        const resolved = text.replace(regex, (match, prefix, variableName) => {
            const variable = constants.find((v: ApplicationVariable) => v.name === variableName);
            if (variable && variable.value) {
                hasVariables = true;
                return variable.value;
            }
            return match; // Keep original if not found
        });

        // If we found and replaced variables, recursively resolve the result
        if (hasVariables && resolved !== text) {
            return resolveNestedVariables(resolved, maxDepth - 1);
        }

        return resolved;
    };

    // Resolve one level of variables (for unwrapping)
    const resolveOneLevel = (text: string): string => {
        const constants = Array.isArray(appVariableContainer.applicationVariables)
            ? appVariableContainer.applicationVariables
            : [];

        const regex = /\{\{(variable|prompt|constant)\.([A-Za-z0-9_]+)\}\}/g;

        return text.replace(regex, (match, prefix, variableName) => {
            const variable = constants.find((v: ApplicationVariable) => v.name === variableName);
            if (variable && variable.value) {
                return variable.value;
            }
            return match; // Keep original if not found
        });
    };

    const findVariableReferences = (text: string): VariableReference[] => {
        const regex = /\{\{(variable|prompt|constant)\.([A-Za-z0-9_]+)\}\}/g;
        const references: VariableReference[] = [];
        let match;

        const constants = Array.isArray(appVariableContainer.applicationVariables)
            ? appVariableContainer.applicationVariables
            : [];

        while ((match = regex.exec(text)) !== null) {
            const prefix = match[1]; // "variable", "prompt", or "constant"
            const variableName = match[2];

            const appConstant = constants.find(
                (v: ApplicationVariable) => v.name === variableName
            );

            references.push({
                start: match.index,
                end: match.index + match[0].length,
                text: match[0],
                prefix,
                variableName,
                variable: appConstant,
                isValid: !!appConstant
            });
        }

        return references;
    };

    // HTML/Text conversion utilities
    const generateHighlightedHTML = (text: string): string => {
        if (!text) return '';

        const references = findVariableReferences(text);

        // Helper function to escape HTML and preserve structure
        const escapeAndFormatText = (str: string): string => {
            return str.replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\n/g, '<br>');
        };

        if (references.length === 0) {
            // No variables, just escape HTML and convert newlines
            return escapeAndFormatText(text);
        }

        // Create a map of unique variable names to color indices
        const uniqueVarNames = new Set<string>();
        references.forEach(ref => {
            if (ref.isValid) {
                uniqueVarNames.add(ref.variableName);
            }
        });
        const varNameToColorIndex = new Map<string, number>();
        Array.from(uniqueVarNames).forEach((name, index) => {
            varNameToColorIndex.set(name, index);
        });

        let html = '';
        let lastIndex = 0;

        references.forEach((ref) => {
            // Add text before the variable
            if (ref.start > lastIndex) {
                const beforeText = text.substring(lastIndex, ref.start);
                html += escapeAndFormatText(beforeText);
            }

            // Get color index for this variable (alternate between 0 and 1)
            const colorIndex = ref.isValid ? varNameToColorIndex.get(ref.variableName) || 0 : 0;

            // Add the highlighted variable with color index (modulo 2 for alternating colors)
            const className = ref.isValid ? `highlighted-variable highlighted-variable-${colorIndex % 2}` : 'invalid-variable';

            // For display: resolve all nested variables to show the deepest value
            const displayText = ref.variable
                ? resolveNestedVariables(ref.variable.value)
                : ref.text;

            // For tooltip: show the immediate next level (one level unwrapped)
            const nextLevelValue = ref.variable ? ref.variable.value : '';
            const tooltip = nextLevelValue.replace(/"/g, '&quot;');

            const escapedDisplayText = displayText.replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\n/g, '<br>');

            // Store the original variable text for editing functionality
            html += `<span class="${className}" data-tooltip="${tooltip}" data-variable-text="${ref.text.replace(/"/g, '&quot;')}">${escapedDisplayText}</span>`;

            lastIndex = ref.end;
        });

        // Add remaining text
        if (lastIndex < text.length) {
            const remainingText = text.substring(lastIndex);
            html += escapeAndFormatText(remainingText);
        }

        return html;
    };

    const htmlToText = (html: string): string => {
        if (!html) return '';

        // Create a temporary div to parse HTML properly
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Walk through all nodes and extract text content properly
        const extractText = (node: Node, isFirstChild: boolean = false): string => {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.textContent || '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (element.tagName === 'BR') {
                    return '\n';
                } else if (element.tagName === 'DIV') {
                    // For DIV elements that contentEditable creates for new lines
                    // Check if the DIV is empty or contains only a BR
                    const hasOnlyBr = element.childNodes.length === 1 &&
                        element.firstChild?.nodeName === 'BR';

                    if (hasOnlyBr || element.childNodes.length === 0) {
                        // Empty DIV or DIV with just BR - represents a newline
                        return isFirstChild ? '' : '\n';
                    }

                    const content = Array.from(element.childNodes).map((child, idx) =>
                        extractText(child, idx === 0)
                    ).join('');

                    // Add newline before DIV content only if it's not the first element
                    return isFirstChild ? content : '\n' + content;
                } else if (element.tagName === 'P') {
                    // For P elements, similar logic
                    const content = Array.from(element.childNodes).map((child, idx) =>
                        extractText(child, idx === 0)
                    ).join('');
                    return isFirstChild ? content : '\n' + content;
                } else {
                    // For inline elements (like spans), just extract content
                    return Array.from(element.childNodes).map((child, idx) =>
                        extractText(child, idx === 0)
                    ).join('');
                }
            }
            return '';
        };

        const result = Array.from(tempDiv.childNodes).map((node, idx) =>
            extractText(node, idx === 0)
        ).join('');

        return result;
    };

    // Content change handler - just update value
    const handleContentChange = (e: React.FormEvent<HTMLDivElement>) => {
        if (contentEditableRef.current && props.onChange) {
            const htmlContent = contentEditableRef.current.innerHTML;
            const textContent = htmlToText(htmlContent);

            // Update the lastValueRef immediately to prevent external updates
            lastValueRef.current = textContent;

            // Call onChange immediately for responsive UI
            const syntheticEvent = {
                target: { value: textContent, name: props.name },
                currentTarget: { value: textContent, name: props.name }
            } as React.ChangeEvent<any>;
            props.onChange(syntheticEvent);
        }
    };

    // Apply/update highlighting when field loses focus
    const handleBlur = (e: any) => {
        if (contentEditableRef.current) {
            const currentHTML = contentEditableRef.current.innerHTML;
            const textContent = htmlToText(currentHTML);
            const newHTML = generateHighlightedHTML(textContent);

            // Verify that the round-trip conversion preserves the text exactly
            const verifyText = htmlToText(newHTML);

            // Only update if the text content is exactly the same (including newlines)
            if (verifyText === textContent &&
                verifyText.length === textContent.length &&
                (verifyText.match(/\n/g) || []).length === (textContent.match(/\n/g) || []).length) {

                // Update with highlighted content
                contentEditableRef.current.innerHTML = newHTML;
                lastValueRef.current = textContent;
                attachTooltipEvents();
            } else {
                // If conversion would change content, just attach tooltips to existing HTML
                attachTooltipEvents();
            }
        }

        // Call the original onBlur if provided
        if (props.onBlur) {
            props.onBlur(e);
        }
    };

    // Keep highlighting when gaining focus, just save cursor position
    const handleFocus = (e: any) => {
        // Don't remove highlighting - just let the user type
        // The highlighting will be preserved until they blur

        // Call the original onFocus if provided
        if (props.onFocus) {
            props.onFocus(e);
        }
    };

    // Cursor position management utilities
    const saveCursorPosition = (): number => {
        if (!contentEditableRef.current) return 0;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return 0;

        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(contentEditableRef.current);
        preCaretRange.setEnd(range.startContainer, range.startOffset);

        // Create a temporary div to extract HTML from DocumentFragment
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(preCaretRange.cloneContents());
        const textContent = htmlToText(tempDiv.innerHTML);
        return textContent.length;
    };

    const restoreCursorPosition = (position: number) => {
        if (!contentEditableRef.current) return;

        const walker = document.createTreeWalker(
            contentEditableRef.current,
            NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
            {
                acceptNode: (node) => {
                    if (node.nodeType === Node.TEXT_NODE) {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                    if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'BR') {
                        return NodeFilter.FILTER_ACCEPT;
                    }
                    return NodeFilter.FILTER_SKIP;
                }
            }
        );

        let currentPosition = 0;
        let node;

        while (node = walker.nextNode()) {
            if (node.nodeType === Node.TEXT_NODE) {
                const textLength = node.textContent?.length || 0;
                if (currentPosition + textLength >= position) {
                    const range = document.createRange();
                    const selection = window.getSelection();

                    try {
                        const offset = Math.min(Math.max(0, position - currentPosition), textLength);
                        range.setStart(node, offset);
                        range.collapse(true);

                        selection?.removeAllRanges();
                        selection?.addRange(range);

                        // Ensure the contentEditable div has focus
                        contentEditableRef.current?.focus();
                    } catch (e) {
                        // If we can't set the exact position, set it at the end
                        try {
                            const range = document.createRange();
                            const selection = window.getSelection();
                            range.selectNodeContents(contentEditableRef.current);
                            range.collapse(false);
                            selection?.removeAllRanges();
                            selection?.addRange(range);
                        } catch (fallbackError) {
                            // Final fallback - just focus the element
                            contentEditableRef.current?.focus();
                        }
                    }
                    return;
                }
                currentPosition += textLength;
            } else if (node.nodeName === 'BR') {
                // <br> tags represent line breaks, count as 1 character
                if (currentPosition >= position) {
                    const range = document.createRange();
                    const selection = window.getSelection();

                    try {
                        range.setStartBefore(node);
                        range.collapse(true);

                        selection?.removeAllRanges();
                        selection?.addRange(range);

                        contentEditableRef.current?.focus();
                    } catch (e) {
                        // Could not restore cursor position at BR
                    }
                    return;
                }
                currentPosition += 1; // BR counts as one character (newline)
            }
        }

        // If position is beyond content, place cursor at end
        if (position >= currentPosition) {
            try {
                const range = document.createRange();
                const selection = window.getSelection();
                range.selectNodeContents(contentEditableRef.current);
                range.collapse(false);
                selection?.removeAllRanges();
                selection?.addRange(range);
            } catch (e) {
                contentEditableRef.current?.focus();
            }
        }
    };

    // Variable replacement functionality
    const replaceVariableWithValue = (variableText: string, variableValue: string) => {
        if (!contentEditableRef.current || !props.onChange) return;

        // The value prop contains the actual stored text with variable syntax
        // We need to replace in that, not in the displayed HTML
        const currentText = props.value || '';

        // Save cursor position before replacing
        const currentCursorPos = saveCursorPosition();

        // Find the first occurrence of the variable pattern in the stored text
        const variableIndex = currentText.indexOf(variableText);
        if (variableIndex === -1) return; // Variable not found

        // Replace only the first occurrence with the actual value
        const newText = currentText.substring(0, variableIndex) +
            variableValue +
            currentText.substring(variableIndex + variableText.length);

        // Update the content
        const syntheticEvent = {
            target: { value: newText, name: props.name },
            currentTarget: { value: newText, name: props.name }
        } as React.ChangeEvent<any>;

        props.onChange(syntheticEvent);

        // Update the displayed content and restore cursor position
        setTimeout(() => {
            if (contentEditableRef.current) {
                contentEditableRef.current.innerHTML = generateHighlightedHTML(newText);
                lastValueRef.current = newText;

                // Adjust cursor position accounting for the length difference
                const lengthDifference = variableValue.length - variableText.length;
                let newCursorPos = currentCursorPos;
                if (currentCursorPos > variableIndex) {
                    if (currentCursorPos <= variableIndex + variableText.length) {
                        // Cursor was inside the variable, place it at the end of the replacement
                        newCursorPos = variableIndex + variableValue.length;
                    } else {
                        // Cursor was after the variable, adjust for the length difference
                        newCursorPos = currentCursorPos + lengthDifference;
                    }
                }

                restoreCursorPosition(newCursorPos);
                attachTooltipEvents();
            }
        }, 0);

        // Hide tooltip after replacing
        setTooltip(prev => ({ ...prev, visible: false, editable: false }));
    };

    // Tooltip event management
    const attachTooltipEvents = () => {
        if (!contentEditableRef.current) return;

        const highlightedElements = contentEditableRef.current.querySelectorAll('.highlighted-variable');

        highlightedElements.forEach((element) => {
            const tooltipText = element.getAttribute('data-tooltip');
            if (tooltipText) {
                // Remove any existing event listeners to prevent duplicates
                const newElement = element.cloneNode(true) as Element;
                element.parentNode?.replaceChild(newElement, element);

                let timeoutId: NodeJS.Timeout | null = null;

                const showTooltip = (e: MouseEvent) => {
                    isHoveringVariableRef.current = true;
                    if (tooltipTimeoutRef.current) {
                        clearTimeout(tooltipTimeoutRef.current);
                        tooltipTimeoutRef.current = null;
                    }

                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                    const variableElement = e.target as HTMLElement;
                    // Get the original variable text from data attribute
                    const variableText = variableElement.getAttribute('data-variable-text') || '';

                    // Extract variable name from the pattern {{prefix.variableName}}
                    const variableNameMatch = variableText.match(/\{\{(?:variable|prompt|constant)\.([A-Za-z0-9_]+)\}\}/);
                    const variableName = variableNameMatch ? variableNameMatch[1] : '';

                    setTooltip({
                        text: tooltipText,
                        x: rect.left + rect.width / 2, // Center horizontally on the variable
                        y: rect.top + 2, // Position just above the variable with minimal gap
                        visible: true,
                        editable: true,
                        variableText: variableText,
                        variableName: variableName
                    });
                };

                const hideTooltip = () => {
                    isHoveringVariableRef.current = false;
                    // Use a longer delay to allow moving to tooltip
                    const timeoutId = setTimeout(() => {
                        // Only hide if not hovering over tooltip or variable
                        if (!isHoveringTooltipRef.current && !isHoveringVariableRef.current) {
                            setTooltip(prev => ({ ...prev, visible: false, editable: false }));
                        }
                    }, 200); // Longer delay for better UX
                    tooltipTimeoutRef.current = timeoutId;
                };

                newElement.addEventListener('mouseenter', showTooltip);
                newElement.addEventListener('mouseleave', hideTooltip);
            }
        });
    };

    // Render component

    return (
        <OuterWrapper
            className={props.className}
            onClick={(e) => { props.stopPropagation ? e.stopPropagation() : null }}
        >
            {/* Label section */}
            {showLabel && (
                <LabelContainer>
                    {props.label !== undefined && (
                        <Label htmlFor={props.name}>
                            {props.label}
                        </Label>
                    )}
                    {props.canDelete && (
                        <Button
                            additionalClasses={deleteIconStyle}
                            type="button"
                            onClick={props.onDelete}
                            themes={["icon"]}
                            icon={deleteIcon}
                        />
                    )}
                    {props.tooltip !== undefined && (
                        <IconTooltip
                            text={props.tooltip}
                            className={tooltipStyle}
                            place="top"
                        />
                    )}
                    {props.required && (
                        <TooltipWrapper text="Required field">
                            <RequiredLabel>*</RequiredLabel>
                        </TooltipWrapper>
                    )}
                </LabelContainer>
            )}

            {/* Input field section */}
            <FieldContainer>
                {showResetButton && (
                    <ResetButton
                        type="button"
                        onClick={handleReset}
                    >
                        Reset to default
                    </ResetButton>
                )}
                {props.icon !== undefined && <img src={props.icon} />}
                <EditableTextField
                    ref={contentEditableRef}
                    contentEditable={!props.disabled}
                    hasIcon={props.icon != null}
                    onInput={handleContentChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.stopPropagation();
                        }
                    }}
                    suppressContentEditableWarning={true}
                    data-placeholder={props.placeholder}
                    style={{ minHeight: `${(props.minRows || 1) * 1.5}em` }}
                />
            </FieldContainer>

            {/* Tooltip popup */}
            {tooltip.visible && (
                <TooltipPopup
                    style={{
                        left: Math.max(10, Math.min(tooltip.x, window.innerWidth - 420)), // Increased buffer for wider tooltip
                        top: Math.max(10, tooltip.y), // Prevent going above viewport
                        transform: 'translateX(-50%) translateY(-100%)',
                        position: 'fixed'
                    }}
                    onMouseEnter={() => {
                        isHoveringTooltipRef.current = true;
                        // Clear any pending hide timeout and keep tooltip visible
                        if (tooltipTimeoutRef.current) {
                            clearTimeout(tooltipTimeoutRef.current);
                            tooltipTimeoutRef.current = null;
                        }
                        setTooltip(prev => ({ ...prev, visible: true }));
                    }}
                    onMouseLeave={() => {
                        isHoveringTooltipRef.current = false;
                        // Use a longer delay and check both hover states
                        const timeoutId = setTimeout(() => {
                            // Only hide if not hovering over tooltip or variable
                            if (!isHoveringTooltipRef.current && !isHoveringVariableRef.current) {
                                setTooltip(prev => ({ ...prev, visible: false, editable: false }));
                            }
                        }, 200);
                        tooltipTimeoutRef.current = timeoutId;
                    }}
                >
                    <TooltipContentWrapper>
                        <VariableName>{tooltip.variableName}</VariableName>
                        {tooltip.editable && tooltip.variableText && (
                            <>
                                <EditButton
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        replaceVariableWithValue(tooltip.variableText!, tooltip.text);
                                    }}
                                >
                                    <EditIcon src={editIcon} alt="Edit" />
                                    <EditButtonText>
                                        Unlink & Edit
                                    </EditButtonText>
                                </EditButton>
                                <InfoText>
                                    Unlinking keeps this content fixed. Linked content automatically receives the latest assistant improvements.
                                </InfoText>
                            </>
                        )}
                    </TooltipContentWrapper>
                </TooltipPopup>
            )}

            {/* Form error display */}
            {props.useForm && <FieldError name={props.name} />}
        </OuterWrapper>
    );
};

const OuterWrapper = styled.div<{ className?: string }>`
    margin-bottom: 24px;

    &.tight {
        margin-bottom: 8px;
        label {
            margin: 8px 16px 0 16px;
        }
    }
`;

const LabelContainer = styled.div`
    display: flex;
    align-items: center;
`;

const Label = styled.label`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    text-align: left;
    margin: 0 16px;
`;

const RequiredLabel = styled.span`
    margin-right: 0;
    margin-left: auto;
    margin-top: 14px;
    margin-bottom: -20px;
    font-family: Muli;
    font-size: 24px;
    color: ${faded_red};
`;

const FieldContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    border-radius: 5px;
    border: solid 1px ${silver_two}; 
    background: white;
    margin-top: 8px;
    position: relative;
    
    img {
        margin: 16px;
    }
`;

const ResetButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    color: ${color_colors_ocean};
    font-family: Muli;
    font-size: 12px;
    cursor: pointer;
    z-index: 1;
    padding: 4px 8px;
    border-radius: 4px;
    text-decoration: none;
    
    &:hover {
        background-color: ${color_variants_ocean_light_1};
    }
`;

const EditableTextField = styled.div<{ hasIcon?: boolean }>`
    color: ${dark_grey_blue};
    font-family: Muli;
    text-align: left;
    width: 100%;
    border: none; 
    padding: 8px 16px;
    font-size: 14px;
    margin: 12px 0;
    border-left: ${props => props.hasIcon ? `1px solid ${silver_two}` : 'none'};
    background: white;
    outline: none;
    min-height: 1.5em;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-y: auto;
    max-height: 300px;
    line-height: 1.4;
    
    &:empty::before {
        content: attr(data-placeholder);
        color: ${cool_grey};
        font-style: italic;
    }

    .highlighted-variable {
        border-radius: 3px;
        padding: 1px 2px;
        cursor: pointer;
        white-space: pre-wrap;
        word-wrap: break-word;
        color: ${text_blue};
    }

    /* Alternate between two ocean light colors for different variables */
    .highlighted-variable-0 {
        background-color: ${color_variants_ocean_light_2};
    }

    .highlighted-variable-1 {
        background-color: ${color_variants_ocean_xlight};
    }

    .invalid-variable {
        background-color: ${color_variants_red_2};
        color: ${color_colors_decline};
        border-radius: 3px;
        padding: 1px 2px;
        cursor: pointer;
        text-decoration: underline wavy ${color_colors_decline};
        white-space: pre-wrap;
        word-wrap: break-word;
    }

    br {
        display: block;
        margin: 0;
        line-height: 1.4;
    }
`;

const TooltipPopup = styled.div`
    position: fixed;
    background: ${color_shades_darkest};
    color: white;
    padding: 8px;
    border-radius: 6px;
    font-size: 16px;
    font-family: Muli;
    z-index: 1000;
    pointer-events: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: ${color_shades_darkest};
    }
`;

const TooltipContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
`;

const VariableName = styled.span`
    font-family: 'Courier New', monospace;
    font-size: 13px;
    font-weight: 600;
    padding: 2px 6px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
`;

const EditButton = styled.button`
    background: none;
    margin-top: 4px;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: auto;
    width: auto;
    height: 32px;
    flex-shrink: 0;
    transition: background-color 0.2s;
    white-space: nowrap;

    &:hover {
        background-color: rgba(255, 255, 255, 0.2);
    }

    &:active {
        background-color: rgba(255, 255, 255, 0.3);
    }
`;

const EditIcon = styled.img`
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
`;

const EditButtonText = styled.span`
    font-family: Muli;
    font-size: 13px;
    font-weight: 600;
`;

const InfoText = styled.div`
    font-family: Muli;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.85);
    text-align: center;
    line-height: 1.4;
    max-width: 280px;
`;

const InlineVariableName = styled.span`
    font-family: 'Courier New', monospace;
    font-size: 13px;
    font-weight: 600;
    padding: 2px 6px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
`;

const deleteIconStyle = css`
    height: 14px;
    margin: 0;
    margin-top: 24px;
    margin-bottom: 0;
    
    img {
        height: 14px;
    }
`;

const tooltipStyle = css`
    margin-left: 8px;
`;

export default TextAreaField;